"use client";

import React from 'react';
import { ArrowPathIcon, BeakerIcon, WrenchScrewdriverIcon, BuildingOffice2Icon } from '@heroicons/react/24/outline';

const WhySimaxSection = () => {
  const comparisonData = [
    { factor: "Risk-Free Start", largeFirms: false, offshore: false, internal: false, simax: true },
    { factor: "EDW Specialization", largeFirms: false, offshore: false, internal: false, simax: true },
    { factor: "Flexible Pricing", largeFirms: false, offshore: true, internal: true, simax: true },
    { factor: "Full EDW Lifecycle", largeFirms: true, offshore: false, internal: false, simax: true },
    { factor: "Enterprise Tech Stack", largeFirms: true, offshore: false, internal: false, simax: true },
    { factor: "Quick Decision Cycles", largeFirms: false, offshore: true, internal: true, simax: true },
    { factor: "Embedded Expert Model", largeFirms: false, offshore: false, internal: false, simax: true },
    { factor: "Transparency", largeFirms: false, offshore: false, internal: true, simax: true }
  ];

  const differentiators = [
    {
      icon: ArrowPathIcon,
      title: "START SMALL, NO BIG BET",
      description: "We begin with a 2-6 week assessment–giving you scope, cost, and ROI clarity before any major investment. Most vendors demand 6-month commitments upfront."
    },
    {
      icon: BeakerIcon,
      title: "TRIAL OUR CAPABILITY FIRST",
      description: "Unique embedded expert model: place one of our data scientists in your team for 2 months. Proof before purchase. Zero risk."
    },
    {
      icon: WrenchScrewdriverIcon,
      title: "ONE PARTNER, FULL LIFECYCLE",
      description: "Assessment → Architecture → Build → Dashboards → Support. You don't coordinate multiple vendors. We handle everything."
    },
    {
      icon: BuildingOffice2Icon,
      title: "ENTERPRISE QUALITY, MID-MARKET AGILITY",
      description: "We use the same platforms as global IT firms (Snowflake, AWS, Azure)–without the bureaucracy, overhead, or inflated pricing."
    }
  ];

  return (
    <section className="bg-cream py-12 sm:py-16 md:py-24 min-h-0 flex items-center">
      <div className="container mx-auto px-4 sm:px-6 max-w-7xl">
        {/* Headline */}
        <h2 className="font-serif text-navy text-[26px] sm:text-[34px] md:text-[40px] lg:text-[44px] leading-[1.3] font-bold text-left md:text-center mb-4 sm:mb-6">
          What Sets Simax Apart
        </h2>

        {/* Subheadline */}
        <p className="text-charcoal text-[15px] sm:text-base md:text-xl leading-[1.7] text-left md:text-center max-w-4xl md:mx-auto mb-10 sm:mb-16">
          We&apos;re not the biggest EDW vendor. But we&apos;re the most transparent, flexible, and committed to your success.
        </p>

        {/* Comparison Table - horizontal scroll on small screens */}
        <div className="overflow-x-auto -mx-4 sm:mx-0 mb-10 sm:mb-16 scrollbar-thin">
          <div className="min-w-[580px] px-4 sm:px-0">
          <table className="w-full border-collapse bg-white shadow-sm">
            <thead>
              <tr className="bg-navy text-white">
                <th className="border border-gray-300 px-3 sm:px-4 py-2 sm:py-3 text-left text-xs sm:text-sm font-semibold">Factor</th>
                <th className="border border-gray-300 px-2 sm:px-4 py-2 sm:py-3 text-center text-xs sm:text-sm font-semibold">Large IT Firms</th>
                <th className="border border-gray-300 px-2 sm:px-4 py-2 sm:py-3 text-center text-xs sm:text-sm font-semibold">Offshore</th>
                <th className="border border-gray-300 px-2 sm:px-4 py-2 sm:py-3 text-center text-xs sm:text-sm font-semibold">Internal IT</th>
                <th className="border-2 border-[#C5A572] px-2 sm:px-4 py-2 sm:py-3 text-center text-xs sm:text-sm font-semibold bg-gradient-to-b from-[#C5A572]/10 to-[#C5A572]/5 relative">
                  <div className="absolute inset-0 bg-gradient-to-b from-[#C5A572]/10 to-transparent pointer-events-none"></div>
                  <span className="relative z-10 text-[#C5A572] font-bold">Simax</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {comparisonData?.map((row, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="border border-gray-300 px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-charcoal font-medium">{row?.factor}</td>
                  <td className="border border-gray-300 px-2 sm:px-4 py-2 sm:py-3 text-center">
                    <span className={row?.largeFirms ? "text-gold text-base sm:text-lg" : "text-silver-gray text-base sm:text-lg"}>
                      {row?.largeFirms ? "✓" : "✗"}
                    </span>
                  </td>
                  <td className="border border-gray-300 px-2 sm:px-4 py-2 sm:py-3 text-center">
                    <span className={row?.offshore ? "text-gold text-base sm:text-lg" : "text-silver-gray text-base sm:text-lg"}>
                      {row?.offshore ? "✓" : "✗"}
                    </span>
                  </td>
                  <td className="border border-gray-300 px-2 sm:px-4 py-2 sm:py-3 text-center">
                    <span className={row?.internal ? "text-gold text-base sm:text-lg" : "text-silver-gray text-base sm:text-lg"}>
                      {row?.internal ? "✓" : "✗"}
                    </span>
                  </td>
                  <td className="border-2 border-[#C5A572] px-2 sm:px-4 py-2 sm:py-3 text-center bg-gradient-to-b from-[#C5A572]/5 to-white relative shadow-[0_0_20px_rgba(197,165,114,0.15)]">
                    <span className={row?.simax ? "text-[#C5A572] text-lg sm:text-xl font-semibold" : "text-silver-gray text-base sm:text-lg"}>
                      {row?.simax ? "✓" : "✗"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>

        {/* Differentiator Boxes */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          {differentiators?.map((item, index) => {
            const IconComponent = item?.icon;
            return (
              <div
                key={index}
                className="bg-white border-l-4 border-gold rounded-lg p-5 sm:p-6 md:p-8 shadow-[0_2px_8px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_16px_rgba(0,0,0,0.1)] transition-all"
              >
                <IconComponent className="w-8 h-8 sm:w-10 sm:h-10 text-gold mb-3 sm:mb-4" />
                <h3 className="text-navy text-base sm:text-lg font-bold mb-2 sm:mb-3">{item?.title}</h3>
                <p className="text-charcoal text-[14px] sm:text-base leading-[1.7]">{item?.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default WhySimaxSection;